import React from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { BookOpen, ChevronRight, PlayCircle, ArrowLeft } from 'lucide-react';
import { PROGRAMS, SUBJECT_CHAPTERS } from '../config/tutorialCatalogue';
import { useProgress } from '../utils/useProgress';
import { useAlmostThere } from '../utils/useAlmostThere';
import AlmostThereModal from '../components/achievement/AlmostThereModal';
import SubjectProgressBar from '../components/SubjectProgressBar';
import TutorialAccessGuard from '../components/TutorialAccessGuard';
import Footer from './Footer';
import SocialSidebar from '../components/SocialSideBar';
import styles from './TutorialLearning.module.css';

export function LegacyTutorialSubject({ subjectKey }) {
  const programs = PROGRAMS.filter(item => item.subjects.some(subject => subject.subjectKey === subjectKey));
  return programs.length
    ? <TutorialAccessGuard programIds={programs.map(program => program.id)}>
        {programId => <Navigate replace to={`/tutorials/course/${programId}?subject=${subjectKey}`} />}
      </TutorialAccessGuard>
    : <TutorialNotFound />;
}

export function TutorialNotFound() {
  return (
    <main className={styles.page}>
      <div className={styles.emptyState}>
        <BookOpen size={36} />
        <h1>This learning page isn't available</h1>
        <p>Choose a course to explore its subjects and chapters.</p>
        <Link className={styles.primaryLink} to="/tutorials">Browse tutorials</Link>
      </div>
    </main>
  );
}

function SubjectChapters({ program, subject }) {
  const chapters = SUBJECT_CHAPTERS[subject.subjectKey] || [];
  const { progress, getChapterStats, getSubjectStats } = useProgress(subject.subjectKey);
  const nudge = useAlmostThere(subject.subjectKey, progress);
  const stats = getSubjectStats(chapters);
  const lectureCount = chapters.reduce((total, chapter) => total + (chapter.lectures?.length || 0), 0);

  return (
    <>
      {nudge.show && <AlmostThereModal {...nudge.props} onClose={nudge.dismiss} />}
      <section className={styles.subjectHeader} aria-labelledby="subject-heading">
        <div>
          <span className={styles.eyebrow}>YOUR LEARNING PATH</span>
          <h2 id="subject-heading">{subject.title}</h2>
          <p>Build your understanding, one chapter at a time.</p>
          <div className={styles.metrics}>
            <span><BookOpen size={16} /> {chapters.length} chapters</span>
            <span><PlayCircle size={16} /> {lectureCount} lectures</span>
          </div>
        </div>
        <div className={styles.subjectProgress}>
          <SubjectProgressBar stats={stats} accentColor={program.gradFrom} />
          {stats.total === 0 && <p>Content coming soon. Your learning journey starts here.</p>}
        </div>
      </section>
      {subject.comingSoon ? (
        <div className={styles.emptyState}><BookOpen size={32} /><h3>{subject.title} is coming soon</h3><p>Switch subjects to continue learning.</p></div>
      ) : (
        <section aria-label={`${subject.title} chapters`}>
          <div className={styles.sectionHeading}><h3>Explore chapters</h3><span>{chapters.length} chapters in your syllabus</span></div>
          <div className={styles.chapterGrid}>
            {chapters.map((chapter, index) => {
              const chapterStats = getChapterStats(chapter.id, chapter);
              const upcoming = chapterStats.total === 0;
              const cardStatus = upcoming ? 'upcoming' : chapterStats.status === 'completed' ? 'completed' : chapterStats.status === 'in-progress' ? 'inProgress' : 'ready';
              const content = (
                <>
                  <div className={styles.cardTop}>
                    <span className={styles.chapterNumber}>{String(index + 1).padStart(2, '0')}</span>
                    <span className={styles.status}>{upcoming ? 'Coming soon' : chapterStats.status === 'completed' ? 'Completed' : chapterStats.status === 'in-progress' ? 'In progress' : 'Ready to start'}</span>
                  </div>
                  <h4>{chapter.title.replace(/^Chapter\s+\d+\s*:\s*/i, '')}</h4>
                  <p>{chapter.lectures?.length || 0} lectures <span aria-hidden="true"> / </span> {chapter.dpps?.length || 0} practice sets <span aria-hidden="true"> / </span> {chapter.tests?.length || 0} tests</p>
                  <div className={styles.progressLabel}><span>{chapterStats.done}/{chapterStats.total} activities complete</span><strong>{chapterStats.pct}%</strong></div>
                  <progress className={styles.progressTrack} value={chapterStats.pct} max="100" aria-label={`${chapter.title} progress`} />
                  <span className={styles.cardAction}>{upcoming ? 'New content on the way' : chapterStats.status === 'in-progress' ? 'Continue chapter' : 'Open chapter'}{!upcoming && <ChevronRight size={17} />}</span>
                </>
              );
              return upcoming
                ? <div key={chapter.id} className={`${styles.chapterCard} ${styles.upcoming}`}>{content}</div>
                : <Link key={chapter.id} className={`${styles.chapterCard} ${styles[cardStatus]}`} to={`/tutorials/course/${program.id}/${subject.subjectKey}/${chapter.id}`}>{content}</Link>;
            })}
          </div>
        </section>
      )}
    </>
  );
}

export default function TutorialCourse() {
  const { programId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const program = PROGRAMS.find(item => item.id === programId);
  if (!program) return <TutorialNotFound />;
  const requested = searchParams.get('subject');
  const subject = program.subjects.find(item => item.subjectKey === requested) || program.subjects[0];

  return (
    <TutorialAccessGuard programIds={[program.id]}>
    <div className={styles.page} style={{ '--course-accent': program.gradFrom }}>
      <SocialSidebar />
      <main className={styles.wrapper}>
        <Link className={styles.backLink} to="/tutorials"><ArrowLeft size={16} /> All tutorials</Link>
        <header className={styles.courseHeader}>
          <div><span className={styles.eyebrow}>{program.tagline}</span><h1>{program.label} <span>Learning studio</span></h1><p>{program.description}</p></div>
          <span className={styles.courseBadge}><BookOpen size={19} /> {program.stats}</span>
        </header>
        <div className={styles.subjectTabs} role="group" aria-label="Choose a subject">
          {program.subjects.map(item => (
            <button key={item.subjectKey} type="button" aria-pressed={item.subjectKey === subject.subjectKey} onClick={() => setSearchParams({ subject: item.subjectKey })}>
              {item.title}{item.comingSoon && <small>Coming soon</small>}
            </button>
          ))}
        </div>
        <SubjectChapters key={subject.subjectKey} program={program} subject={subject} />
      </main>
      <Footer />
    </div>
    </TutorialAccessGuard>
  );
}
